/* eslint-disable react-hooks/exhaustive-deps */
import type { ReactNode } from "react";
import { useEffect, createContext, useRef, useMemo, useState, useCallback } from "react";
import throttle from "lodash.throttle";

import useDeviceSize from "hooks/useDeviceSize";
import { useExtendedRouter } from "hooks";
import {
  ChakraBox as Box,
  ChakraDrawer as Drawer,
  ChakraDrawerOverlay as DrawerOverlay,
  ChakraDrawerContent as DrawerContent,
  ChakraDrawerCloseButton as DrawerCloseButton,
  chakraUseDisclosure as useDisclosure,
} from "@airhelp/react-ui-components";
import type { MobileMenuContextProviderProps, MobileMenuContextType } from "./MobileMenuContext.d";

export const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

export const MobileMenuContextProvider = ({ children }: MobileMenuContextProviderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [mobileMenuContent, setMobileMenuContent] = useState<ReactNode>(null);
  const btnRef = useRef();
  const { isDesktop } = useDeviceSize();
  const router = useExtendedRouter();

  const closeMobileMenuOnLargeScreen = () => {
    if (isDesktop) onClose();
  };

  useEffect(() => {
    window.addEventListener("resize", throttle(closeMobileMenuOnLargeScreen, 100));
    router.events.on("beforeHistoryChange", onClose);

    return () => {
      window.removeEventListener("resize", closeMobileMenuOnLargeScreen);
      router.events.off("beforeHistoryChange", onClose);
    };
  }, []);

  const openMobileMenu = useCallback(
    (content: ReactNode) => {
      if (content) {
        onOpen();
        setMobileMenuContent(content);
      }
    },
    [onOpen]
  );

  const value = useMemo(
    () => ({
      openMobileMenu,
    }),
    [openMobileMenu]
  );

  return (
    <MobileMenuContext.Provider value={value}>
      {children}
      <Drawer isOpen={isOpen} onClose={onClose} finalFocusRef={btnRef}>
        <Box display={{ lg: "none" }}>
          <DrawerOverlay bg="rgba(69, 84, 115, 0.7)" />
          <DrawerContent>
            <DrawerCloseButton
              size="lg"
              color="primary.900"
              _hover={{ color: "primary.500", bg: "white" }}
              _active={{ color: "primary.700", bg: "white" }}
              rounded="full"
              mt={2}
              mr={1}
            />
            {mobileMenuContent}
          </DrawerContent>
        </Box>
      </Drawer>
    </MobileMenuContext.Provider>
  );
};
